import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import MetaTags from './MetaTags';
import spotlightService from '../../services/SpotlightService';
import { generatePageSEO } from '../../utils/SEOUtils';
import { optimizeImageUrl, getResponsiveImagePreset } from '../../utils/ImageUtils';

/**
 * DynamicMetaTags Component
 * 
 * Handles dynamic meta tag generation for both social media sharing and SEO indexing
 * based on URL parameters. Specifically designed for PDP deep linking via URLs like:
 * https://www.chillfree.tv/?channel=the-boat-builder
 * 
 * This component:
 * 1. Monitors URL changes for 'channel' parameter
 * 2. Fetches channel data when parameter is present
 * 3. Generates comprehensive meta tags for social sharing AND search engine indexing
 * 4. Creates SEO-friendly titles, descriptions, and structured data
 * 5. Falls back to default homepage meta tags when no parameter
 */
const DynamicMetaTags = () => {
  const location = useLocation();
  const [metaData, setMetaData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const updateMetaTags = async () => {
      // Parse URL parameters
      const searchParams = new URLSearchParams(location.search);
      const channelParam = searchParams.get('channel');

      // If no channel parameter, use default homepage meta tags
      if (!channelParam) {
        const defaultMeta = generatePageSEO('homepage');
        const currentOrigin = window.location.origin;
        setMetaData({
          title: defaultMeta.title,
          description: defaultMeta.description,
          image: `${currentOrigin}/assets/images/chillfree-social-share.jpg`,
          url: currentOrigin,
          type: 'website',
        });
        return;
      }

      // Fetch channel data for dynamic meta tags
      setLoading(true);
      try {
        console.log('DynamicMetaTags: Fetching channel data for:', channelParam);
        
        // Fetch channel by slug
        const response = await spotlightService.getChannelBySlug(channelParam);
        
        console.log('DynamicMetaTags: API response:', response);
        
        if (response?.channels?.[0]) {
          const channel = response.channels[0];
          console.log('DynamicMetaTags: Channel data found:', channel.title);
          
          // Determine content type
          const isMovie = channel.programming_type === 'Movie' || 
                         channel.type === 'movie' || 
                         channel.content_type === 'movie';
          
          const isSeries = channel.programming_type === 'Series' || 
                          channel.type === 'series' || 
                          channel.content_type === 'series' ||
                          (channel.childchannels && channel.childchannels.length > 0);

          // Generate title based on content type
          let title;
          if (isMovie) {
            title = `Watch ${channel.title} - Free Movie Streaming | ChillFree TV`;
          } else if (isSeries) {
            title = `Stream ${channel.title} Episodes - Free TV Series | ChillFree TV`;
          } else {
            title = `${channel.title} - Free Streaming | ChillFree TV`;
          }

          // Generate description
          let description = channel.description || '';
          if (description.length > 160) {
            description = description.substring(0, 157) + '...';
          }
          
          // Add call-to-action if description is short
          if (!description) {
            if (isMovie) {
              description = `Watch ${channel.title} for free on ChillFree TV. Stream classic movies and timeless entertainment without subscription fees.`;
            } else if (isSeries) {
              description = `Stream all episodes of ${channel.title} for free on ChillFree TV. Classic TV series and timeless shows available now.`;
            } else {
              description = `Watch ${channel.title} for free on ChillFree TV. Stream quality entertainment without subscription fees.`;
            }
          }

          // Get optimized image for social sharing
          let socialImage = null;
          const rawImageUrl = channel.spotlight_poster || 
                             channel.poster || 
                             channel.thumb || 
                             channel.image?.spotlight_poster || 
                             channel.image?.poster || 
                             channel.image?.thumb;

          if (rawImageUrl) {
            // Use direct optimization for social media (1200x630 is optimal for FB, Twitter, LinkedIn)
            socialImage = optimizeImageUrl(rawImageUrl, {
              width: 1200,
              height: 630,
              webp: false // Social media crawlers prefer JPG/PNG
            });
            
            // Ensure the image URL is absolute (starts with http/https)
            // optimizeImageUrl should return absolute URLs for dotstudioPRO images,
            // but we need to ensure it's absolute for social media crawlers
            if (socialImage && !socialImage.startsWith('http')) {
              // If it's a relative URL, make it absolute
              socialImage = `${window.location.origin}${socialImage.startsWith('/') ? '' : '/'}${socialImage}`;
            }
          }

          // Fallback to default ChillFree image if no channel image
          if (!socialImage) {
            const currentOrigin = window.location.origin;
            socialImage = `${currentOrigin}/assets/images/chillfree-social-share.jpg`;
          }

          // Generate keywords based on channel data
          const keywords = [];
          if (channel.title) keywords.push(channel.title);
          if (channel.genres && channel.genres.length > 0) {
            keywords.push(...channel.genres.slice(0, 3));
          }
          if (isMovie) keywords.push('free movie', 'stream movie', 'watch online');
          if (isSeries) keywords.push('free TV series', 'stream episodes', 'watch series');
          keywords.push('ChillFree TV', 'free streaming', 'no subscription');

          // Generate SEO-optimized title for search engines
          let seoTitle;
          if (isMovie) {
            seoTitle = `Watch ${channel.title} (${channel.year || 'Classic'}) Free Online | ChillFree TV`;
          } else if (isSeries) {
            seoTitle = `${channel.title} - Stream All Episodes Free | ChillFree TV`;
          } else {
            seoTitle = `${channel.title} - Free Streaming | ChillFree TV`;
          }

          // Generate comprehensive SEO description
          let seoDescription = description;
          if (!seoDescription || seoDescription.length < 120) {
            if (isMovie) {
              seoDescription = `Watch ${channel.title}${channel.year ? ` (${channel.year})` : ''} free online on ChillFree TV. ${channel.genres?.slice(0,2).join(', ') || 'Classic'} movie streaming without subscription. High-quality entertainment at no cost.`;
            } else if (isSeries) {
              seoDescription = `Stream all episodes of ${channel.title} free on ChillFree TV. ${channel.genres?.slice(0,2).join(', ') || 'Classic'} TV series with no subscription required. Binge-watch your favorite shows online.`;
            } else {
              seoDescription = `Watch ${channel.title} free on ChillFree TV. Quality entertainment streaming without subscription fees. Discover classic content and timeless shows.`;
            }
          }

          // Set meta data with enhanced SEO
          // Use current origin to support both production and staging environments
          const currentOrigin = window.location.origin;
          setMetaData({
            title: seoTitle,
            description: seoDescription,
            image: socialImage,
            url: `${currentOrigin}/?channel=${channelParam}`,
            type: isMovie ? 'video.movie' : isSeries ? 'video.tv_show' : 'website',
            keywords: keywords.join(', '),
            // Enhanced SEO data
            seoData: {
              contentType: isMovie ? 'movie' : isSeries ? 'series' : 'content',
              title: channel.title,
              year: channel.year,
              genres: channel.genres || [],
              rating: channel.rating || channel.content_rating,
              duration: channel.duration,
              language: channel.language || 'English',
              country: channel.country || 'US',
              cast: channel.actors || [],
              director: channel.directors || [],
              slug: channelParam
            },
            // Additional structured data for better SEO
            additionalMeta: {
              'article:author': 'ChillFree TV',
              'article:publisher': 'https://www.facebook.com/ChillFreeTV',
              'og:video:type': 'text/html',
              'og:video:width': '1280',
              'og:video:height': '720',
              'og:locale': 'en_US',
              'og:site_name': 'ChillFree TV',
              ...(channel.year && { 'video:release_date': channel.year }),
              ...(channel.duration && { 'video:duration': channel.duration }),
              ...(channel.rating && { 'video:rating': channel.rating }),
              // SEO-specific meta tags
              'robots': 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
              'googlebot': 'index, follow',
              'bingbot': 'index, follow',
              'author': 'ChillFree TV',
              'publisher': 'ChillFree TV',
              'content-language': 'en-US',
              'distribution': 'global',
              'rating': 'general',
              'revisit-after': '7 days'
            }
          });

        } else {
          // Channel not found, use default meta tags
          console.log('DynamicMetaTags: No channel data found in response for:', channelParam);
          console.log('DynamicMetaTags: Response structure:', JSON.stringify(response, null, 2));
          
          const defaultMeta = generatePageSEO('homepage');
          const currentOrigin = window.location.origin;
          setMetaData({
            title: defaultMeta.title,
            description: defaultMeta.description,
            image: `${currentOrigin}/assets/images/chillfree-social-share.jpg`,
            url: currentOrigin,
            type: 'website',
          });
        }
      } catch (error) {
        console.error('DynamicMetaTags: Error fetching channel data for:', channelParam);
        console.error('DynamicMetaTags: Error details:', error);
        console.error('DynamicMetaTags: Error message:', error.message);
        console.error('DynamicMetaTags: Error status:', error.status);
        
        // Fallback to default meta tags on error
        const defaultMeta = generatePageSEO('homepage');
        const currentOrigin = window.location.origin;
        setMetaData({
          title: defaultMeta.title,
          description: defaultMeta.description,
          image: `${currentOrigin}/assets/images/chillfree-social-share.jpg`,
          url: currentOrigin,
          type: 'website',
        });
      } finally {
        setLoading(false);
      }
    };

    updateMetaTags();
  }, [location.search]); // Re-run when URL parameters change

  // Don't render anything while loading or if no meta data
  if (loading || !metaData) {
    return null;
  }

  return (
    <>
      {/* Primary Meta Tags for Social Sharing */}
      <MetaTags
        title={metaData.title}
        description={metaData.description}
        image={metaData.image}
        url={metaData.url}
        type={metaData.type}
      />
      
      {/* Additional SEO Meta Tags - Simplified */}
      {metaData.keywords && (
        <Helmet>
          <meta name="keywords" content={metaData.keywords} />
        </Helmet>
      )}
      
      {/* Canonical URL */}
      <Helmet>
        <link rel="canonical" href={metaData.url} />
      </Helmet>
      
      {/* Basic SEO Meta Tags */}
      <Helmet>
        <meta name="robots" content="index, follow" />
        <meta name="author" content="ChillFree TV" />
      </Helmet>
    </>
  );
};

export default DynamicMetaTags;

